/*
 * Wovenday -- currency display converter (estimate only).
 *
 * This script NEVER changes the real price or checkout currency: Stripe always charges in USD.
 * It only re-formats the visible price text for visitors from the UK, Canada or Australia, so
 * they see the price in their own currency automatically. Exactly one price is shown at a time.
 *
 * Currency is fully automatic, based on the visitor's detected country (window.wvdCountry, set
 * server-side in layout/theme.liquid from {{ localization.country.iso_code }}): UK sees GBP,
 * Canada sees CAD, Australia sees AUD, everyone else (including the US) sees USD. There is no
 * manual selector and no stored override -- the currency is simply recomputed from the visitor's
 * country on every page load.
 *
 * Rates: fetched once per session from https://open.er-api.com/v6/latest/USD (no API key) and
 * cached in localStorage (wvd_fx_rates) for 6 hours to avoid refetching on every page view.
 *
 * Rendering: any element with a data-usd-cents="<integer>" attribute (Shopify money objects are
 * already integers in cents) has its own textContent overwritten in place with a single
 * Intl.NumberFormat-formatted price in the currently-effective currency.
 */
(function () {
  'use strict';

  var FX_STORAGE_KEY = 'wvd_fx_rates';
  var FX_MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours
  var FX_URL = 'https://open.er-api.com/v6/latest/USD';

  var COUNTRY_TO_CURRENCY = { GB: 'GBP', CA: 'CAD', AU: 'AUD' };

  var state = {
    rates: null,
    currency: 'USD'
  };

  function safeGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable, no-op */ }
  }

  function detectCurrency() {
    var country = window.wvdCountry;
    return (country && COUNTRY_TO_CURRENCY[country]) || 'USD';
  }

  function loadCachedRates() {
    var raw = safeGet(FX_STORAGE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.timestamp || !parsed.data) return null;
      if (Date.now() - parsed.timestamp > FX_MAX_AGE_MS) return null;
      return parsed.data;
    } catch (e) {
      return null;
    }
  }

  function fetchRates() {
    var cached = loadCachedRates();
    if (cached) {
      state.rates = cached;
      renderAll();
      return;
    }
    if (!('fetch' in window)) return;

    fetch(FX_URL)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (data && data.rates) {
          state.rates = data;
          safeSet(FX_STORAGE_KEY, JSON.stringify({ timestamp: Date.now(), data: data }));
          renderAll();
        }
      })
      .catch(function () {
        // Display-only estimate: fail silently, never surface an error to the shopper.
      });
  }

  function formatMoney(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function priceForCurrency(usdCents, currency) {
    var usd = usdCents / 100;
    if (currency === 'USD') {
      return formatMoney(usd, 'USD');
    }
    if (!state.rates || !state.rates.rates || typeof state.rates.rates[currency] !== 'number') {
      return null;
    }
    return formatMoney(usd * state.rates.rates[currency], currency);
  }

  function renderAll() {
    var currency = state.currency;
    var elements = document.querySelectorAll('[data-usd-cents]');

    elements.forEach(function (el) {
      var cents = parseInt(el.getAttribute('data-usd-cents'), 10);
      if (isNaN(cents)) return;

      var formatted = priceForCurrency(cents, currency);
      if (formatted === null) {
        // Rates not available yet for a non-USD currency: leave USD showing until they load.
        formatted = priceForCurrency(cents, 'USD');
      }
      el.textContent = formatted;
    });
  }

  function init() {
    state.currency = detectCurrency();
    renderAll();
    if (state.currency !== 'USD') fetchRates();
  }

  window.WvdCurrency = {
    refresh: renderAll,
    getCurrency: function () { return state.currency; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
